<template>
	<Dialog v-model:visible="visibleModel" header="Activity Log" :modal="true" appendTo="body" position="center"
		:style="{ width: '720px', maxWidth: '95vw' }" class="spe-v2-activity-dialog" :baseZIndex="1500" @show="onShow"
		@hide="onHide">
		<div v-if="loading" class="flex justify-content-center p-5">
			<ProgressSpinner />
		</div>
		<div v-else-if="!timelineEvents.length" class="p-4 text-center text-color-secondary">
			Belum ada activity log.
		</div>
		<Timeline v-else :value="timelineEvents" align="alternate" class="spe-v2-activity-timeline">
			<template #marker="slotProps">
				<span class="spe-v2-activity-marker" :style="{ backgroundColor: slotProps.item.color }">
					<i :class="slotProps.item.icon" />
				</span>
			</template>
			<template #opposite="slotProps">
				<div class="spe-v2-activity-opposite">{{ slotProps.item.date }}</div>
			</template>
			<template #content="slotProps">
				<div class="spe-v2-activity-item">
					<div class="spe-v2-activity-item__title">{{ slotProps.item.title }}</div>
					<div v-if="slotProps.item.details.length" class="spe-v2-activity-item__details">
						<div v-for="(detail, index) in slotProps.item.details" :key="index">
							<span class="spe-v2-activity-item__label">{{ detail.label }}:</span>
							<span>{{ detail.value }}</span>
						</div>
					</div>
				</div>
			</template>
		</Timeline>
		<template #footer>
			<Button label="Close" icon="pi pi-times" class="p-button-outlined" @click="visibleModel = false" />
		</template>
	</Dialog>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import SpeV2Service from '../../service/SpeV2Service';

const props = defineProps({
	visible: { type: Boolean, default: false },
	scoringId: { type: [String, Number], default: null },
});

const emit = defineEmits(['update:visible']);

const loading = ref(false);
const timelineEvents = ref([]);

const visibleModel = computed({
	get: () => props.visible,
	set: (value) => emit('update:visible', value),
});

const ACTIVITY_META = {
	PURCHASING_SEND: {
		title: 'Purchasing Send to Supplier',
		icon: 'pi pi-send',
		color: '#3b82f6',
	},
	SUPPLIER_ACKNOWLEDGE: {
		title: 'Supplier Acknowledge',
		icon: 'pi pi-check',
		color: '#10b981',
	},
	PURCHASING_CONFIRM: {
		title: 'Purchasing Proxy Confirm',
		icon: 'pi pi-verified',
		color: '#f59e0b',
	},
	PURCHASING_PROXY_COMPLETE: {
		title: 'Purchasing Proxy Confirm',
		icon: 'pi pi-verified',
		color: '#f59e0b',
	},
	PURCHASING_DISMISS: {
		title: 'Dismissed',
		icon: 'pi pi-ban',
		color: '#94a3b8',
	},
	PURCHASING_DISMISS_REQUEST: {
		title: 'Dismissed',
		icon: 'pi pi-ban',
		color: '#94a3b8',
	},
	APPROVER_DISMISS_REJECT: {
		title: 'Reject Dismiss',
		icon: 'pi pi-times',
		color: '#ef4444',
	},
	APPROVER_DISMISS_APPROVE: {
		title: 'Approve Dismiss',
		icon: 'pi pi-check-circle',
		color: '#8b5cf6',
	},
};

const HIDDEN_DETAIL_KEYS = new Set(['action', 'scoring id', 'vendor id', 'company', 'user id']);

const parseDescription = (description) => {
	if (!description) return [];

	return String(description)
		.split('|')
		.map((part) => part.trim())
		.filter(Boolean)
		.map((part) => {
			const separatorIndex = part.indexOf(':');
			if (separatorIndex === -1) {
				return { label: 'Info', value: part };
			}

			return {
				label: part.slice(0, separatorIndex).trim().replace(/_/g, ' '),
				value: part.slice(separatorIndex + 1).trim(),
			};
		})
		.filter((item) => !HIDDEN_DETAIL_KEYS.has(item.label.toLowerCase()));
};

const formatActivityDate = (value) => {
	if (!value) return '-';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return date.toLocaleString('id-ID');
};

const mapActivityToTimeline = (activity) => {
	const meta = ACTIVITY_META[activity.type] || {
		title: activity.type || 'Activity',
		icon: 'pi pi-circle',
		color: '#64748b',
	};

	return {
		title: meta.title,
		icon: meta.icon,
		color: meta.color,
		date: formatActivityDate(activity.activity_date),
		details: parseDescription(activity.description),
	};
};

const loadActivities = async () => {
	if (!props.scoringId) {
		timelineEvents.value = [];
		return;
	}

	loading.value = true;
	try {
		const res = await SpeV2Service.getActivity(props.scoringId);
		const rows = res?.status === 200 && Array.isArray(res.data?.data) ? res.data.data : [];
		timelineEvents.value = rows.map(mapActivityToTimeline);
	} catch (err) {
		timelineEvents.value = [];
	}
	loading.value = false;
};

const onShow = () => {
	loadActivities();
};

const onHide = () => {
	timelineEvents.value = [];
};

watch(
	() => [props.visible, props.scoringId],
	([visible, scoringId]) => {
		if (visible && scoringId) {
			loadActivities();
		}
	}
);
</script>

<style scoped>
.spe-v2-activity-marker {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 2rem;
	height: 2rem;
	border-radius: 999px;
	color: #fff;
	box-shadow: 0 2px 8px rgba(15, 23, 42, 0.15);
}

.spe-v2-activity-item {
	padding: 0.75rem 1rem;
	border-radius: 0.75rem;
	background: #fff;
	border: 1px solid #e2e8f0;
	box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
}

.spe-v2-activity-item__title {
	font-weight: 700;
	color: #0f172a;
}

.spe-v2-activity-opposite {
	font-size: 0.8125rem;
	color: #64748b;
	padding: 0.5rem 0.75rem;
	white-space: nowrap;
}

.spe-v2-activity-item__details {
	margin-top: 0.75rem;
	padding: 0.75rem;
	border-radius: 0.75rem;
	background: #f8fafc;
	font-size: 0.875rem;
	color: #334155;
}

.spe-v2-activity-item__label {
	font-weight: 600;
	margin-right: 0.35rem;
	text-transform: capitalize;
}
</style>

<style>
.spe-v2-activity-dialog .p-dialog-content {
	max-height: 70vh;
	overflow-y: auto;
}

.spe-v2-activity-timeline .p-timeline-event-opposite {
	flex: 1;
}

.spe-v2-activity-timeline .p-timeline-event-content {
	flex: 1;
}

@media screen and (max-width: 960px) {
	.spe-v2-activity-timeline .p-timeline-event:nth-child(even) {
		flex-direction: row !important;
	}

	.spe-v2-activity-timeline .p-timeline-event:nth-child(even) .p-timeline-event-content {
		text-align: left !important;
	}

	.spe-v2-activity-timeline .p-timeline-event-opposite {
		flex: 0;
	}
}
</style>
